import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StudentLayout from "../../layouts/StudentLayout";
import { Arrow } from "../../components/Icons";
import SessionDetailsModal from "../../components/student/SessionDetailsModal";
import { useGetStudentDemoSessionRequestsQuery } from "../../services/demosession/demoSessionApi";

const formatTime12h = (time24) => {
  if (!time24 || typeof time24 !== "string") return "";
  const [rawH, rawM] = time24.split(":");
  const hours = Number(rawH);
  const minutes = Number(rawM);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time24;
  const period = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const formatSessionDisplayTime = (sessionDate, startTime) => {
  if (!sessionDate) return "";

  const date = new Date(sessionDate);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const timePart = startTime ? `${formatTime12h(startTime)} PKT` : "";

  if (isToday) {
    return timePart ? `Today – ${timePart}` : "Today";
  }

  const datePart = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return timePart ? `${datePart} - ${timePart}` : datePart;
};

const DEFAULT_AVATAR =
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=80&q=80";

const formatTableDate = (preferredDate) => {
  if (!preferredDate) return "—";
  const date = new Date(preferredDate);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "pending") {
    return "bg-[#FEF3C7] text-[#B45309]";
  }
  if (normalized === "approved") {
    return "bg-[#DCFCE7] text-[#166534]";
  }
  if (normalized === "rejected") {
    return "bg-[#FEE2E2] text-[#B91C1C]";
  }
  return "bg-[#F3F4F6] text-[#4B5563]";
};

const mapDemoRequestToSessionCard = (request) => {
  const courseTitle = request?.course?.courseTitle || "Course";
  const instructor = request?.instructor;
  const hasInstructor = Boolean(instructor && (instructor.firstName || instructor.lastName));
  const instructorName = hasInstructor
    ? `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim()
    : "Not Assigned";
  const displayTime = formatSessionDisplayTime(
    request?.preferredDate,
    request?.preferredTime,
  );

  return {
    id: request?._id || request?.id,
    title: "Demo Session Requested!",
    description: hasInstructor
      ? `Your demo session has been scheduled by admin with Instructor ${instructorName}`
      : "Your demo session request is pending approval. An instructor will be assigned soon.",
    schedule: courseTitle ? `${displayTime} • ${courseTitle}` : displayTime,
    displayTime,
    sessionId: request?._id || request?.id || "—",
    type: "Demo Session",
    category: request?.category || request?.course?.courseCategory?.[0] || "—",
    level: "—",
    subCategory:
      request?.subcategory || request?.course?.courseSubCategory || "—",
    ageGroup: "—",
    course: courseTitle,
    time: request?.preferredTime || "—",
    date: formatTableDate(request?.preferredDate),
    longDate: request?.preferredDate
      ? new Date(request.preferredDate).toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "—",
    instructorName,
    instructorEmail: instructor?.email || "—",
    instructorAvatar:
      instructor?.profilePicture?.secureUrl ||
      instructor?.profilePicture ||
      DEFAULT_AVATAR,
    meetingLink: request?.demoSessionLink || "",
    status: request?.status || "pending",
  };
};

// const formatCountdown = (totalSeconds) => {
//   const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
//   const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
//   const seconds = String(totalSeconds % 60).padStart(2, "0");
//   return `${hours}:${minutes}:${seconds}`;
// };

const Calender = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.07961 1.33329V2.66663H9.91961V1.33329H11.1996V2.66663H13.7596C13.9388 2.66663 14.0903 2.73107 14.214 2.85996C14.3377 2.98885 14.3996 3.14663 14.3996 3.33329V14C14.3996 14.1866 14.3377 14.3444 14.214 14.4733C14.0903 14.6022 13.9388 14.6666 13.7596 14.6666H2.23961C2.06041 14.6666 1.90894 14.6022 1.78521 14.4733C1.66148 14.3444 1.59961 14.1866 1.59961 14V3.33329C1.59961 3.14663 1.66148 2.98885 1.78521 2.85996C1.90894 2.73107 2.06041 2.66663 2.23961 2.66663H4.79961V1.33329H6.07961ZM13.1196 7.33329H2.87961V13.3333H13.1196V7.33329ZM9.94521 8.09329L10.8412 9.03996L7.67961 12.3333L5.41401 9.97329L6.32281 9.03996L7.67961 10.4533L9.94521 8.09329ZM4.79961 3.99996H2.87961V5.99996H13.1196V3.99996H11.1996V4.66663H9.91961V3.99996H6.07961V4.66663H4.79961V3.99996Z" fill="white"/>
</svg>

)

const UpcomingSessions = () => {
  const navigate = useNavigate();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");

  const {
    data: demoSessionsResponse,
    isLoading,
    isError,
  } = useGetStudentDemoSessionRequestsQuery();

  const allSessions = useMemo(() => {
    const data = demoSessionsResponse?.data;
    if (!Array.isArray(data)) return [];
    return data.map(mapDemoRequestToSessionCard);
  }, [demoSessionsResponse?.data]);

  const pendingSessions = useMemo(
    () => allSessions.filter((item) => item.status === "pending"),
    [allSessions],
  );

  const previousSessions = useMemo(
    () => allSessions.filter((item) => item.status !== "pending"),
    [allSessions],
  );

  const tableSessions =
    activeTab === "upcoming" ? pendingSessions : previousSessions;

  const openSessionDetails = (session) => {
    setSelectedSession({
      ...session,
      time: session.time !== "—" ? formatTime12h(session.time) : "—",
      date: session.longDate,
    });
    setShowDetailsModal(true);
  };

  return (
    <StudentLayout>
      <section className="overflow-x-hidden px-4 pb-16 pt-10 sm:px-5 sm:pt-16 lg:px-12 2xl:px-[71px]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-3 sm:gap-4 text-black"
          aria-label="Go back"
        >
          <Arrow />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-[-1%] text-[#1D2026]">
            Back to Dashboard
          </h2>
        </button>

        <div className="mt-10 sm:mt-20 space-y-6 sm:space-y-8 font-['Roboto']">
          {isLoading ? (
            <p className="text-base text-[#6E7485]">Loading demo sessions...</p>
          ) : isError ? (
            <p className="text-base text-[#C62828]">
              Failed to load demo session requests.
            </p>
          ) : pendingSessions.length === 0 ? (
            <p className="text-base text-[#6E7485]">
              No pending demo session requests right now.
            </p>
          ) : (
            pendingSessions.map((session) => (
              <div
                key={session.id}
                className="flex flex-col gap-4 rounded-lg border border-[#F4A410] bg-gradient-to-r from-[#C8EAD41C] to-[#DCFCE71C] p-4 sm:gap-6 sm:p-[17px] lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3 sm:items-center">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#FF9573]">
                    <Calender />
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg text-[#171717]">
                      {session.title}
                    </h3>
                    <p className="break-words text-sm sm:text-base text-[#BCBCBC]">
                      {session.description}
                    </p>
                    <p className="mt-1 break-words text-sm text-[#BCBCBC]">
                      {session.schedule}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => openSessionDetails(session)}
                  className="w-full shrink-0 rounded-lg bg-[#FF6636] px-4 py-2.5 text-base text-white transition-colors hover:bg-[#e65a2e] lg:mr-8 lg:w-auto"
                >
                  View Details
                </button>
              </div>
            ))
          )}

          {/* Join demo article commented out */}

          <div className="bg-white py-3 pl-4 shadow-[0_-1px_3.8px_0_#00000040] sm:pl-8">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-1 sm:gap-[62px]">
              <button
                type="button"
                onClick={() => setActiveTab("upcoming")}
                className={`whitespace-nowrap text-base leading-10 tracking-[-1%] sm:text-xl sm:leading-[48px] ${
                  activeTab === "upcoming"
                    ? "font-semibold text-[#111827]"
                    : "font-medium text-[#9CA3AF]"
                }`}
              >
                Upcoming Sessions
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("previous")}
                className={`whitespace-nowrap text-base leading-10 tracking-[-1%] sm:text-xl sm:leading-[48px] ${
                  activeTab === "previous"
                    ? "font-semibold text-[#111827]"
                    : "font-medium text-[#9CA3AF]"
                }`}
              >
                Previous Sessions
              </button>
            </div>

            {/* Mobile cards */}
            <div className="mt-4 space-y-3 pr-4 md:hidden">
              {isLoading ? (
                <p className="py-6 text-sm text-[#6E7485]">Loading sessions...</p>
              ) : isError ? (
                <p className="py-6 text-sm text-[#C62828]">
                  Failed to load sessions.
                </p>
              ) : tableSessions.length === 0 ? (
                <p className="py-6 text-sm text-[#6E7485]">
                  {activeTab === "upcoming"
                    ? "No upcoming sessions found."
                    : "No previous sessions found."}
                </p>
              ) : (
                tableSessions.map((session) => (
                  <div
                    key={`card-${activeTab}-${session.id}`}
                    className="rounded-xl border border-[#E5E7EB] bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#111827]">
                          {session.date}
                        </p>
                        <p className="text-sm text-[#9CA3AF]">{session.time}</p>
                      </div>
                      <span
                        className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-medium capitalize ${getStatusBadgeClass(
                          session.status,
                        )}`}
                      >
                        {session.status}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-3">
                      <img
                        src={session.instructorAvatar}
                        alt={session.instructorName}
                        className="h-8 w-8 shrink-0 rounded-full object-cover"
                      />
                      <span className="min-w-0 truncate text-sm font-semibold text-[#111827]">
                        {session.instructorName}
                      </span>
                    </div>

                    <p className="mt-3 break-words text-sm font-semibold text-[#111827]">
                      {session.course}
                    </p>

                    <button
                      type="button"
                      onClick={() => openSessionDetails(session)}
                      className="mt-3 text-sm font-medium text-[#2563EB] hover:underline"
                    >
                      View
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Desktop / tablet table */}
            <div className="-mx-4 hidden overflow-x-auto px-4 sm:-mx-0 sm:px-0 md:block">
              <table className="mt-4 w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-[#E5E7EB] text-left font-['Inter'] text-xs font-semibold uppercase tracking-[0.6px] text-[#606161]">
                    <th className="whitespace-nowrap px-2 py-3 font-medium">
                      Date & Time
                    </th>
                    <th className="whitespace-nowrap px-2 py-3 font-medium">
                      Instructor
                    </th>
                    <th className="whitespace-nowrap px-2 py-3 font-medium">
                      Course
                    </th>
                    <th className="whitespace-nowrap px-2 py-3 font-medium">
                      Status
                    </th>
                    <th className="whitespace-nowrap px-2 py-3 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-2 py-8 text-sm text-[#6E7485]"
                      >
                        Loading sessions...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-2 py-8 text-sm text-[#C62828]"
                      >
                        Failed to load sessions.
                      </td>
                    </tr>
                  ) : tableSessions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-2 py-8 text-sm text-[#6E7485]"
                      >
                        {activeTab === "upcoming"
                          ? "No upcoming sessions found."
                          : "No previous sessions found."}
                      </td>
                    </tr>
                  ) : (
                    tableSessions.map((session) => (
                      <tr
                        key={`${activeTab}-${session.id}`}
                        className="border-b border-[#E5E7EB]"
                      >
                        <td className="whitespace-nowrap px-2 py-4 align-middle">
                          <p className="text-sm font-semibold text-[#111827]">
                            {session.date}
                          </p>
                          <p className="text-sm text-[#9CA3AF]">{session.time}</p>
                        </td>
                        <td className="px-2 py-4 align-middle">
                          <div className="flex min-w-0 items-center gap-3">
                            <img
                              src={session.instructorAvatar}
                              alt={session.instructorName}
                              className="h-8 w-8 shrink-0 rounded-full object-cover"
                            />
                            <span className="max-w-[140px] truncate text-sm font-semibold text-[#111827] lg:max-w-none">
                              {session.instructorName}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-4 align-middle">
                          <p className="max-w-[180px] break-words text-sm font-semibold text-[#111827] lg:max-w-[280px]">
                            {session.course}
                          </p>
                        </td>
                        <td className="px-2 py-4 align-middle">
                          <span
                            className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium capitalize ${getStatusBadgeClass(
                              session.status,
                            )}`}
                          >
                            {session.status}
                          </span>
                        </td>
                        <td className="px-2 py-4 align-middle">
                          <button
                            type="button"
                            onClick={() => openSessionDetails(session)}
                            className="text-sm font-medium text-[#2563EB] hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <SessionDetailsModal
        open={showDetailsModal}
        session={selectedSession}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSession(null);
        }}
        onJoinMeeting={() => {
          if (!selectedSession?.meetingLink) return;
          window.open(selectedSession.meetingLink, "_blank", "noreferrer");
        }}
      />
    </StudentLayout>
  );
};

export default UpcomingSessions;
